import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Card } from "react-bootstrap";
import { FaPrint, FaDownload, FaSearch, FaUser, FaMoneyBillWave, FaFilter, FaFileInvoice, FaPhone, FaMapMarkerAlt, FaShoppingCart, FaBuilding } from "react-icons/fa";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const WorkerLedger = () => {
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    transactionType: "all",
    name: ""
  });
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sample ledger data - In production, this would come from API
  const [ledgerData, setLedgerData] = useState([]);

  const fetchSuppliers = async () => {
    try {
      // setLoading(true);
      // setError("");
      const response = await api.post("/worker_ledger_report.php", {});

      if (response.data.head.code === 200) {
        setLedgerData(response.data.body);
      } else {
        // setError(response.data.head.msg || "Failed to fetch delivery notes");
        setLedgerData([]);
      }
    } catch (err) {
      console.error("Error fetching delivery notes:", err);
      // setError("Failed to load delivery notes. Please try again.");
      setLedgerData([]);
    } finally {
      // setLoading(false);
    }

    try {
      // setLoading(true);
      // setError(null);

      // Note: The customer_list.php expects JSON input, so we need to send an empty object
      const response = await api.post("/job_worker_list.php", {});

      if (response.data.head.code === 200) {

        setSuppliers(response.data.body);
      } else {
        // setError(response.data.head.msg || "Failed to fetch customers");
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      // setError("Failed to load customers. Please try again.");
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Parse YYYY-MM-DD to Date
const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

// Parse DD-MM-YYYY to Date (if your ledger uses this format)
const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

// Filter ledger based on search and date range
const filteredLedger = ledgerData.filter((entry) => {
  const entryDate = entry.date ? parseDDMMYYYY(entry.date) : null;

  // 📅 Date filter
  const from = filters.startDate ? parseYYYYMMDD(filters.startDate) : null;
  const to = filters.endDate ? parseYYYYMMDD(filters.endDate) : null;
  if (to) to.setHours(23, 59, 59, 999); // include full day

  const matchesDate =
    !entryDate || ((!from || entryDate >= from) && (!to || entryDate <= to));

  // 🔍 Search filter
  const query = search?.toLowerCase() || "";
  const matchesSearch =
    !query ||
    entry.worker_name?.toLowerCase().includes(query) ||
    entry.doc_no?.toLowerCase().includes(query) 
    // entry.description?.toLowerCase().includes(query)
    ;

  return matchesDate && matchesSearch;
});


  // Calculate opening balance (balance before the selected date range)
  const getOpeningBalance = () => {
    if (!selectedSupplier || !dateFrom) return 0;

    const previousEntries = ledgerData.filter(entry =>
      entry.supplierId === parseInt(selectedSupplier) && entry.date < dateFrom
    );

    if (previousEntries.length === 0) return 0;

    return previousEntries[previousEntries.length - 1].balance;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    return filteredLedger.reduce((totals, entry) => {
      return {
        totalDebit: Number(totals.totalDebit) + Number(entry.debit),
        totalCredit: Number(totals.totalCredit) + Number(entry.credit),
        closingBalance: entry.balance
      };
    }, { totalDebit: 0, totalCredit: 0, closingBalance: 0 });
  };

  const totals = calculateTotals();
    const netBalance = totals.totalDebit - totals.totalCredit;

  const openingBalance = getOpeningBalance();

  // Custom transaction type classes
  const getTransactionTypeClass = (type) => {
    const typeClassMap = {
      purchase: 'status-badge status-approved',
      payment: 'status-badge status-completed',
      debit_note: 'status-badge status-rejected',
      credit_note: 'status-badge status-pending',
      adjustment: 'status-badge status-progress',
      advance: 'status-badge status-default'
    };

    const labels = {
      purchase: 'Purchase',
      payment: 'Payment',
      debit_note: 'Debit Note',
      credit_note: 'Credit Note',
      adjustment: 'Adjustment',
      advance: 'Advance'
    };

    return {
      className: typeClassMap[type] || 'status-badge status-default',
      label: labels[type] || type
    };
  };

  // Get selected supplier details
  const getSelectedSupplier = () => {
    return suppliers.find(s => s.name === selectedSupplier);
  };

  const selectedSupplierDetails = getSelectedSupplier();

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentLedger,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredLedger, 10);
const formatToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";

  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
};
const formatDDMMYYYY = (date) => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
};
const getMonthStartEnd = () => {
  const now = new Date();

  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    from_date: formatDDMMYYYY(start),
    to_date: formatDDMMYYYY(end),
  };
};
  const handlePrint = () => {
    const fromDate = formatToDDMMYYYY(filters.startDate) || getMonthStartEnd().from_date;
  const toDate = formatToDDMMYYYY(filters.endDate) || getMonthStartEnd().to_date;

  console.log("Print From:", fromDate);
  console.log("Print To:", toDate);
  // /vinayaga_wires
    window.open(`/workerledgerprint/${search}/${fromDate}/${toDate}`, '_blank');
  };

  const handleExport = () => {
    if (!selectedSupplierDetails) return;

    const headers = ['Date', 'Type', 'Document No', 'Description', 'Debit', 'Credit', 'Balance'];
    const csvData = filteredLedger.map(entry => [
      entry.date,
      getTransactionTypeClass(entry.type).label,
      entry.documentNo,
      entry.description,
      entry.debit,
      entry.credit,
      entry.balance
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `supplier-ledger-${selectedSupplierDetails.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSelectedSupplier("");
    setDateFrom("");
    setDateTo("");
    setSearch("");
  };

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Job Work Ledger</h2>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading Workers...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Worker Ledger</h2>
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchSuppliers}>
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Job Work Ledger</h2>

      {/* Filters Section */}
      <div className="filter-section mb-3 p-3 bg-light rounded shadow-sm">
        <Row className="align-items-center mb-2">
          <Col>
            <h5 className="mb-0">
              <FaFilter className="me-2" />
              Filters
            </h5>
          </Col>
        </Row>

        <Row className="g-2">
          <Col xs={12} md={3} lg={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={3} lg={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4} lg={4} className="mb-2 mb-md-0">
            <div className="position-relative">
              <Form.Label>Search</Form.Label>
              <Form.Control
                placeholder="Search by Worker Name,Document No"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch("");
                }}
              />
              {/* <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" /> */}
            </div>
          </Col>
<Col xs={12} md={2} lg={2} className="mb-2 mb-md-0">
            <div className="position-relative">
              <Form.Label> Print</Form.Label>
              <Button className="btn-submit" onClick={handlePrint}>
                <FaPrint size={20} />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Supplier Information Table */}
      {selectedSupplierDetails && (
        <div className="mb-4">
          <h5 className="mb-3">
            <FaBuilding className="me-2 text-primary" />
            Worker Details
          </h5>
          <div className="table-responsive">
            <Table bordered className="customer-table customer-details-table">
              <thead className="table-primary">
                <tr>
                  <th width="5%">S.No</th>
                  <th width="20%">Name</th>
                  <th width="15%">Mobile</th>
                  <th width="20%">GST Number</th>
                  <th width="15%">State</th>
                  <th width="25%">Address</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center">1</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <FaBuilding className="me-2 text-primary" />
                      <strong>{selectedSupplierDetails.name}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <FaPhone className="me-2 text-success" />
                      {selectedSupplierDetails.mobile || "N/A"}
                    </div>
                  </td>
                  <td>
                    <code>{selectedSupplierDetails.gst_number || "N/A"}</code>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <FaMapMarkerAlt className="me-2 text-danger" />
                      {selectedSupplierDetails.state || "N/A"}
                    </div>
                  </td>
                  <td>
                    <div className="text-muted small">
                      {selectedSupplierDetails.address || "N/A"}
                      {selectedSupplierDetails.city && `, ${selectedSupplierDetails.city}`}
                      {selectedSupplierDetails.pin_code && ` - ${selectedSupplierDetails.pin_code}`}
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* Balance Summary */}
      {selectedSupplier && (
        <Row className="mb-3">
          <Col xs={12}>
            <div className="p-3 border rounded bg-light">
              <Row>
                <Col md={3}>
                  <strong>Opening Balance:</strong>
                  <span className={openingBalance >= 0 ? 'text-success' : 'text-danger'}>
                    ₹{Math.abs(openingBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    {openingBalance >= 0 ? ' Cr' : ' Dr'}
                  </span>
                </Col>
                <Col md={3}>
                  <strong>Total Debit:</strong> ₹{totals.totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Col>
                <Col md={3}>
                  <strong>Total Credit:</strong> ₹{totals.totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Col>
                <Col md={3}>
                  <strong>Closing Balance:</strong>
                  <span className={totals.closingBalance >= 0 ? 'text-success' : 'text-danger'}>
                    ₹{Math.abs(totals.closingBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    {totals.closingBalance >= 0 ? ' Cr' : ' Dr'}
                  </span>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}

      {/* Ledger Table */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <h5 className="mb-3">
            <FaFileInvoice className="me-2 text-primary" />
            Ledger Transactions
          </h5>
          <Table responsive className="customer-table customer-ledger-table">
            <thead>
              <tr>
                <th width="10%">Date</th>
                <th>Worker Name</th>
                <th width="12%">Document No</th>
                {/* <th width="28%">Description</th> */}
                <th width="12%" className="text-end">Debit (₹)</th>
                <th width="12%" className="text-end">Credit (₹)</th>
                <th width="16%" className="text-end">Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {/* Opening Balance Row */}
              {selectedSupplier && openingBalance !== 0 && (
                <tr className="table-warning">
                  <td colSpan="4" className="text-end fw-bold">Opening Balance</td>
                  <td colSpan="3" className="text-end fw-bold">
                    <span className={openingBalance >= 0 ? 'text-success' : 'text-danger'}>
                      ₹{Math.abs(openingBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      {openingBalance >= 0 ? ' Cr' : ' Dr'}
                    </span>
                  </td>
                </tr>
              )}

              {filteredLedger
                .map((entry, index) => {
                  const transactionType = getTransactionTypeClass(entry.type);
                  return (
                    <tr key={entry.id}>
                      <td className="text-center">{entry.date}</td>
                      {/* <td className="type-column">
                        <span className={transactionType.className}>
                          {transactionType.label}
                        </span>
                      </td> */}
                      <td>
                        <strong>{entry.worker_name}</strong>
                      </td>
                      <td>
                        <strong>{entry.doc_no}</strong>
                      </td>
                      {/* <td>
                        <small>{entry.description}</small>
                      </td> */}
                      <td className="text-end">
                        {entry.debit > 0 && (
                          <span className="text-danger fw-bold">
                            ₹{entry.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        {entry.credit > 0 && (
                          <span className="text-success fw-bold">
                            ₹{entry.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </td>
                      <td className="text-end fw-bold">
                        <span className={entry.balance >= 0 ? 'text-success' : 'text-danger'}>
                          ₹{Math.abs(entry.balance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          {/* {entry.balance >= 0 ? ' Cr' : ' Dr'} */}
                        </span>
                      </td>
                    </tr>
                  );
                })}

              {selectedSupplier && filteredLedger.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="text-muted">
                      <FaFileInvoice size={48} className="mb-3" />
                      <p>No transactions found for selected period</p>
                      <small>Try adjusting your date range</small>
                    </div>
                  </td>
                </tr>
              )}

              {/* {!selectedSupplier && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="text-muted">
                      <FaBuilding size={48} className="mb-3" />
                      <p>Please select a supplier to view ledger</p>
                    </div>
                  </td>
                </tr>
              )} */}
            </tbody>

            {filteredLedger.length > 0 && (
              <tfoot className="table-warning">
                {/* className="table-secondary fw-bold" */}
                <tr className="fw-bold">
                  <td colSpan="3" className="text-end">
                    TOTAL
                  </td>

                  <td className="text-end text-danger">
                    ₹{totals.totalDebit.toLocaleString('en-IN', {
                      minimumFractionDigits: 2
                    })}
                  </td>

                  <td className="text-end text-success">
                    ₹{totals.totalCredit.toLocaleString('en-IN', {
                      minimumFractionDigits: 2
                    })}
                  </td>

                  <td className="text-end">
                    <span className={netBalance >= 0 ? "text-danger" : "text-success"}>
                      ₹{Math.abs(netBalance).toLocaleString('en-IN', {
                        minimumFractionDigits: 2
                      })} {netBalance >= 0 ? " Dr" : " Cr"}

                    </span>
                  </td>
                </tr>
              </tfoot>

            )}
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </Container>
  );
};

export default WorkerLedger;